import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Clock,
  Bell,
  Mail,
  MessageSquare,
  AlertTriangle,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { reminderApi } from "@/services/api";

export const ReminderConfig = ({
  envelopeId = null,
  templateId = null,
  isOrgDefault = false,
  onSave,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [config, setConfig] = useState({
    enabled: true,
    type: "interval",
    intervalHours: 24,
    fixedDays: [1, 3, 7],
    maxReminders: 5,
    escalationEnabled: false,
    escalationAfter: 3,
    escalationEmail: "",
    smsEnabled: false,
    customSubject: "",
    customMessage: "",
  });

  // Fetch existing schedule
  const { data: existingSchedule, isLoading } = useQuery({
    queryKey: ["reminder-schedule", envelopeId, templateId, isOrgDefault],
    queryFn: () => {
      if (envelopeId) {
        return reminderApi.getEffective(envelopeId).then((r) => r.data?.data);
      }
      return null;
    },
    enabled: !!envelopeId,
  });

  useEffect(() => {
    if (existingSchedule) {
      setConfig({
        enabled: existingSchedule.enabled ?? true,
        type: existingSchedule.type || "interval",
        intervalHours: existingSchedule.intervalHours || 24,
        fixedDays: existingSchedule.fixedDays || [1, 3, 7],
        maxReminders: existingSchedule.maxReminders || 5,
        escalationEnabled: !!existingSchedule.escalationEmail,
        escalationAfter: existingSchedule.escalationAfter || 3,
        escalationEmail: existingSchedule.escalationEmail || "",
        smsEnabled: existingSchedule.smsEnabled || false,
        customSubject: existingSchedule.customSubject || "",
        customMessage: existingSchedule.customMessage || "",
      });
    }
  }, [existingSchedule]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data) => {
      if (isOrgDefault) {
        return reminderApi.createOrgDefault(data);
      } else if (templateId) {
        return reminderApi.createForTemplate(templateId, data);
      } else if (envelopeId) {
        return reminderApi.createForEnvelope(envelopeId, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["reminder-schedule"]);
      toast.success("Reminder settings saved");
      onSave?.();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          "Failed to save reminder settings",
      );
    },
  });

  const handleSave = () => {
    const payload = {
      enabled: config.enabled,
      type: config.type,
      intervalHours: config.type === "interval" ? config.intervalHours : null,
      fixedDays: config.type === "fixed_days" ? config.fixedDays : null,
      maxReminders: config.maxReminders,
      escalationEmail: config.escalationEnabled ? config.escalationEmail : null,
      escalationAfter: config.escalationEnabled ? config.escalationAfter : null,
      smsEnabled: config.smsEnabled,
      customSubject: config.customSubject || null,
      customMessage: config.customMessage || null,
    };
    saveMutation.mutate(payload);
  };

  const addFixedDay = () => {
    const maxDay = Math.max(...config.fixedDays, 0);
    setConfig({
      ...config,
      fixedDays: [...config.fixedDays, maxDay + 7].sort((a, b) => a - b),
    });
  };

  const removeFixedDay = (day) => {
    setConfig({
      ...config,
      fixedDays: config.fixedDays.filter((d) => d !== day),
    });
  };

  const renderInterval = () => {
    if (config?.intervalHours === 24) {
      return "Once per day";
    }
    return config?.intervalHours < 24
      ? `${Math.round(24 / config?.intervalHours)} times per day`
      : `Every ${Math.round(config?.intervalHours / 24)} day(s)`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Bell className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium">{t("reminders.automatic")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("reminders.sendToPendingSigners")}
            </p>
          </div>
        </div>
        <Switch
          checked={config.enabled}
          onCheckedChange={(checked) =>
            setConfig({ ...config, enabled: checked })
          }
        />
      </div>

      {config.enabled && (
        <>
          <Separator />

          {/* Reminder Type */}
          <div className="space-y-3">
            <Label>{t("reminders.schedule")}</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setConfig({ ...config, type: "interval" })}
                className={`p-4 border-2 rounded-xl text-left transition-all ${
                  config.type === "interval"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/50"
                }`}
              >
                <RefreshCw
                  className={`h-5 w-5 mb-2 ${config.type === "interval" ? "text-primary" : "text-muted-foreground"}`}
                />
                <div className="font-medium">
                  {t("reminders.regularInterval")}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("reminders.sendEveryXHours")}
                </div>
              </button>

              <button
                type="button"
                onClick={() => setConfig({ ...config, type: "fixed_days" })}
                className={`p-4 border-2 rounded-xl text-left transition-all ${
                  config.type === "fixed_days"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/50"
                }`}
              >
                <Clock
                  className={`h-5 w-5 mb-2 ${config.type === "fixed_days" ? "text-primary" : "text-muted-foreground"}`}
                />
                <div className="font-medium">{t("reminders.fixedDays")}</div>
                <div className="text-sm text-muted-foreground">
                  {t("reminders.sendOnSpecificDays")}
                </div>
              </button>
            </div>
          </div>

          {/* Interval Hours */}
          {config.type === "interval" && (
            <div className="space-y-2">
              <Label htmlFor="interval">{t("reminders.sendEvery")}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="interval"
                  type="number"
                  value={config.intervalHours}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      intervalHours: parseInt(e.target.value) || 24,
                    })
                  }
                  min="1"
                  max="168"
                  className="w-24"
                />
                <span className="text-muted-foreground">hours</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {renderInterval()}
              </p>
            </div>
          )}

          {/* Fixed Days */}
          {config.type === "fixed_days" && (
            <div className="space-y-2">
              <Label>{t("reminders.sendOnDays")}</Label>
              <div className="flex flex-wrap gap-2">
                {config.fixedDays.map((day) => (
                  <Badge
                    key={day}
                    variant="secondary"
                    className="pl-3 pr-1 py-1.5 gap-1"
                  >
                    Day {day}
                    <button
                      onClick={() => removeFixedDay(day)}
                      className="p-0.5 rounded-full hover:bg-muted-foreground/20 transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addFixedDay}
                  className="border-dashed"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Day
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Days after the document was sent
              </p>
            </div>
          )}

          {/* Max Reminders */}
          <div className="space-y-2">
            <Label htmlFor="maxReminders">
              {t("reminders.maxPerRecipient")}
            </Label>
            <Input
              id="maxReminders"
              type="number"
              value={config.maxReminders}
              onChange={(e) =>
                setConfig({
                  ...config,
                  maxReminders: parseInt(e.target.value) || 5,
                })
              }
              min="1"
              max="20"
              className="w-24"
            />
          </div>

          <Separator />

          {/* Escalation */}
          <div className="p-4 rounded-xl bg-muted/50 border border-border space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <span className="font-medium">{t("reminders.escalation")}</span>
              </div>
              <Switch
                checked={config.escalationEnabled}
                onCheckedChange={(checked) =>
                  setConfig({ ...config, escalationEnabled: checked })
                }
              />
            </div>

            {config.escalationEnabled && (
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="escalationAfter">
                    {t("reminders.escalateAfterHow")}
                  </Label>
                  <Input
                    id="escalationAfter"
                    type="number"
                    value={config.escalationAfter}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        escalationAfter: parseInt(e.target.value) || 3,
                      })
                    }
                    min="1"
                    max={config.maxReminders}
                    className="w-24"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="escalationEmail">
                    {t("reminders.escalationEmail")}
                  </Label>
                  <Input
                    id="escalationEmail"
                    type="email"
                    value={config.escalationEmail}
                    onChange={(e) =>
                      setConfig({ ...config, escalationEmail: e.target.value })
                    }
                    placeholder="manager@company.com"
                  />
                </div>
              </div>
            )}
          </div>

          {/* SMS */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border border-border">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-5 w-5 text-emerald-500" />
              <div>
                <span className="font-medium">{t("sms.reminders")}</span>
                <p className="text-sm text-muted-foreground">
                  {t("reminders.sendSmsAlso")}
                </p>
              </div>
            </div>
            <Switch
              checked={config.smsEnabled}
              onCheckedChange={(checked) =>
                setConfig({ ...config, smsEnabled: checked })
              }
            />
          </div>

          <Separator />

          {/* Custom Message */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customSubject">
                {t("envelopes.customSubjectOptional")}
              </Label>
              <Input
                id="customSubject"
                type="text"
                value={config.customSubject}
                onChange={(e) =>
                  setConfig({ ...config, customSubject: e.target.value })
                }
                placeholder={t("placeholders.reminderSubject")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customMessage">
                {t("envelopes.customMessageOptional")}
              </Label>
              <Textarea
                id="customMessage"
                value={config.customMessage}
                onChange={(e) =>
                  setConfig({ ...config, customMessage: e.target.value })
                }
                placeholder={t("placeholders.reminderBodyPersonal")}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Available variables: {"{{recipientName}}"},{" "}
                {"{{envelopeTitle}}"}, {"{{senderName}}"}, {"{{dueDate}}"}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-border">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Settings
        </Button>
      </div>
    </div>
  );
};

// Compact inline version for envelope details page
export const ReminderConfigInline = ({ envelopeId, recipientId }) => {
  const queryClient = useQueryClient();

  const sendMutation = useMutation({
    mutationFn: () => reminderApi.sendImmediate(envelopeId, recipientId),
    onSuccess: () => {
      queryClient.invalidateQueries(["reminder-history", envelopeId]);
    },
  });

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => sendMutation.mutate()}
      disabled={sendMutation.isPending}
    >
      {sendMutation.isPending ? (
        <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
      ) : (
        <Bell className="mr-1.5 h-3 w-3" />
      )}
      Send Reminder
    </Button>
  );
};

// History component
export const ReminderHistory = ({ envelopeId }) => {
  const { data: history, isLoading } = useQuery({
    queryKey: ["reminder-history", envelopeId],
    queryFn: () =>
      reminderApi.getHistory(envelopeId).then((r) => r.data?.data?.items || []),
    enabled: !!envelopeId,
  });

  const renderVariant = (item) => {
    if (item?.status === "sent") {
      return "default";
    }
    return item?.status === "failed" ? "destructive" : "secondary";
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!history?.length) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        No reminders sent yet
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {history.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border"
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-1.5 rounded-full ${
                item.type === "email" ? "bg-blue-500/10" : "bg-emerald-500/10"
              }`}
            >
              {item.type === "email" ? (
                <Mail className="h-4 w-4 text-blue-500" />
              ) : (
                <MessageSquare className="h-4 w-4 text-emerald-500" />
              )}
            </div>
            <div>
              <div className="text-sm font-medium">{item.recipientEmail}</div>
              <div className="text-xs text-muted-foreground">
                {new Date(item.sentAt).toLocaleString()}
              </div>
            </div>
          </div>
          <Badge variant={renderVariant(item)}>{item.status}</Badge>
        </div>
      ))}
    </div>
  );
};

export default ReminderConfig;
